import { useEffect, useId, useState, type FormEvent } from 'react';
import type { Package, PackageMessage } from '@mypoz/shared';
import {
  normalisePackageMessages,
  packageMessagesFor,
  sanitiseMessageContent,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { cn, Sheet } from '@mypoz/ui';

import { fetchPackageMessageRows, postPackageMessage } from '@/features/packages/api.js';
import { useWarehouse } from '@/features/warehouses/index.js';
import { useToast } from '@/features/toast/index.js';
import { buttonClass, inputClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';

/**
 * One message bubble — left-aligned on a neutral background for a warehouse message (W2U), right-
 * aligned on a brand-tinted one for the customer's own words (U2W). Matches the mentor mockup's
 * alignment convention; the customer's side carries no sender line since it's implicitly "you".
 */
function MessageBubble({
  message,
  warehouseLabel,
}: {
  message: PackageMessage;
  warehouseLabel: string;
}) {
  const fmt = useFormat();
  const fromWarehouse = message.direction === 'W2U';

  return (
    <div className={cn('flex', fromWarehouse ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2.5',
          fromWarehouse ? 'bg-surface-alt' : 'bg-brand-soft',
        )}
      >
        <p className="text-xs text-muted">
          {fromWarehouse ? `${warehouseLabel} · ` : null}
          {fmt.dateTime(message.createdAt)}
        </p>
        <p className="mt-1 text-sm text-ink whitespace-pre-wrap">
          {sanitiseMessageContent(message.content)}
        </p>
      </div>
    </div>
  );
}

export interface PackageMessagesSheetProps {
  package: Package;
  onClose: () => void;
}

/**
 * MYP-38's thread view for one package — opened from `PackageFormSheet`'s "Messages" footer link.
 * The mentor mockup shows it over the bare list rather than stacked on top of the form sheet, so
 * `Packages.tsx` closes that sheet first and opens this one in its place (its own `?panel=messages`
 * state) rather than swapping this in as a second mode of the same `Sheet` instance.
 *
 * Reads `get_packages_msg` live since MYP-40, fetched when the sheet opens rather than alongside
 * the package list: a customer reads one thread at a time and the endpoint takes a single package
 * id, so loading every thread up front would be dozens of requests for one the reader wanted.
 *
 * Sending posts to `post_packages_msg` (MYP-101) and re-fetches the thread on success rather than
 * trusting a local echo — the endpoint's success response carries no payload (see
 * `postPackageMessage`'s doc comment in `api.ts`), so there is no server id or timestamp to
 * fabricate one from here. A failed send leaves the typed reply in the box rather than clearing
 * it, so nothing typed is lost; a send that succeeds but whose refetch fails gets its own message,
 * since the warehouse did receive it even though this screen could not immediately show it.
 *
 * The thread itself renders safely as of MYP-39: `sanitiseMessageContent` strips the warehouse's
 * raw Word-pasted HTML down to plain text in `MessageBubble` below, rather than rendering it as
 * HTML, which is what would actually turn this staff-authored field into an XSS risk.
 *
 * The reply box only exists on this screen — `NotificationThreadPanel`'s general notification
 * thread stays read-only, which is what the note beneath the reply box is there to explain.
 */
export function PackageMessagesSheet({ package: pkg, onClose }: PackageMessagesSheetProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const replyId = useId();
  const [reply, setReply] = useState('');
  const [messages, setMessages] = useState<readonly PackageMessage[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // Same StrictMode/in-flight guard the providers use — only the newest fetch may land.
    let current = true;

    setMessages(undefined);
    setFailed(false);

    fetchPackageMessageRows(pkg.id).then(
      (rows) => {
        if (current) setMessages(normalisePackageMessages(rows));
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[packages] could not load the message thread', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [pkg.id]);

  const thread = packageMessagesFor(messages ?? [], pkg.id);
  const warehouse = useWarehouse(pkg.warehouseId);
  // The raw id when the vendor no longer lists that warehouse — see `useWarehouse`. A bubble
  // attributed to "4" is poor, but better than one attributed to nobody.
  const warehouseLabel = warehouse ? warehouse.label : pkg.warehouseId;
  const title = t('packages.messages.title', { tracking: pkg.trackingNumber });

  async function handleSend(event: FormEvent) {
    event.preventDefault();
    const trimmed = reply.trim();
    if (trimmed === '' || sending) return;

    setSending(true);
    try {
      await postPackageMessage(pkg.id, trimmed);
      // Cleared only once the warehouse actually has it — see the failure branch below, which
      // deliberately does not clear the box.
      setReply('');

      try {
        const rows = await fetchPackageMessageRows(pkg.id);
        setMessages(normalisePackageMessages(rows));
      } catch (cause) {
        // The reply is already sent; only the refresh failed. A generic send-failure toast here
        // would tell the customer to retry something that already worked.
        console.error('[packages] sent, but could not refresh the message thread', cause);
        toast.show(t('packages.messages.refreshFailed'));
      }
    } catch (cause) {
      // Left in the box on failure: clearing it would read as if Send silently did nothing, when
      // it actually failed and the customer's words would otherwise be lost.
      console.error('[packages] could not send the reply', cause);
      toast.show(t('packages.messages.sendFailed'));
    } finally {
      setSending(false);
    }
  }

  return (
    <Sheet
      title={title}
      onClose={onClose}
      closeLabel={t('packages.messages.closeDialog', { title })}
    >
      {messages === undefined && !failed ? (
        <p className="text-sm text-muted">{t('packages.messages.loading')}</p>
      ) : failed ? (
        <p className="text-sm text-danger">{t('packages.messages.failed')}</p>
      ) : thread.length > 0 ? (
        <div className="grid gap-3">
          {thread.map((message) => (
            <MessageBubble key={message.id} message={message} warehouseLabel={warehouseLabel} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted">{t('packages.messages.empty')}</p>
      )}

      <form onSubmit={handleSend} className="mt-4 flex items-center gap-2">
        <label htmlFor={replyId} className="sr-only">
          {t('packages.messages.replyPlaceholder')}
        </label>
        <input
          id={replyId}
          type="text"
          value={reply}
          placeholder={t('packages.messages.replyPlaceholder')}
          onChange={(event) => setReply(event.target.value)}
          className={inputClass()}
        />
        <button
          type="submit"
          disabled={reply.trim() === '' || sending}
          className={buttonClass(
            'primary',
            'shrink-0 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {t('packages.messages.send')}
        </button>
      </form>

      <p className="mt-2 text-xs text-muted">{t('packages.messages.replyNote')}</p>
    </Sheet>
  );
}
